import React, { useEffect, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { Plus } from "lucide-react";
import { TopBar } from "@/components/TopBar";
import { QuickAddBar } from "@/components/QuickAddBar";
import { useAuth } from "@/context/AuthContext";
import { useRegisterPullToRefresh } from "@/lib/useRegisterPullToRefresh";
import { CalendarView } from "./Plan/CalendarView";
import { TodosView } from "./Plan/TodosView";
import { ChoresView } from "./Plan/ChoresView";
import { PollsView } from "./Plan/PollsView";
import { CreateSheet } from "./Plan/CreateSheet";

const SUBTABS = [
  { id: "calendar", label: "Calendar" },
  { id: "chores", label: "Chores" },
  { id: "polls", label: "Polls" },
  { id: "todos", label: "To-dos" },
];

// Map a URL path segment after /plan/ to a tab id. Lets us write
// <Route path="/plan/chores" /> and have the segmented control auto-
// switch instead of always landing on Calendar (user feedback Feb 2026:
// "Polls chores and todo menu is broken" — they were hitting the routes
// but the tab state was driven by ?tab= alone).
const PATH_TO_TAB = {
  chores: "chores",
  polls: "polls",
  todos: "todos",
  calendar: "calendar",
};

export default function Plan() {
  const { family } = useAuth();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const pathSegment = location.pathname.split("/")[2] || "";
  const tab = PATH_TO_TAB[pathSegment] || params.get("tab") || "calendar";
  const setTab = (id) => setParams({ tab: id }, { replace: true });

  const [createOpen, setCreateOpen] = useState(false);
  const [createSeedAt, setCreateSeedAt] = useState(null); // Date when tapping a time slot
  const [refreshKey, setRefreshKey] = useState(0);

  // Allow other screens (Today's DaySheet) to deep-link to "open the create
  // sheet" via ?create=1 — strip the param once consumed so a refresh
  // doesn't keep re-opening it.
  useEffect(() => {
    if (params.get("create") === "1" && !createOpen) {
      setCreateOpen(true);
      const next = new URLSearchParams(params);
      next.delete("create");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [params]);

  // Plan's children (Calendar/Chores/Polls/Todos) reload when refreshKey
  // bumps. Pull-to-refresh just bumps the key — every sub-view's useEffect
  // re-runs its load() against the current family + filters.
  useRegisterPullToRefresh(() => {
    setRefreshKey((k) => k + 1);
    // Return a tiny delay so the pull indicator finishes its spin
    return new Promise((res) => setTimeout(res, 350));
  });

  const onCreated = () => {
    setRefreshKey((k) => k + 1);
    setCreateOpen(false);
    setCreateSeedAt(null);
  };

  const openCreateAt = (date) => {
    setCreateSeedAt(date);
    setCreateOpen(true);
  };

  return (
    <>
      <TopBar
        title="Plan"
        subtitle="What's coming up"
        right={
          <button
            data-testid="plan-add"
            onClick={() => setCreateOpen(true)}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-primary text-primary-foreground tap shadow-sm active:scale-95 transition-transform"
            aria-label="Add"
          >
            <Plus className="w-5 h-5" strokeWidth={2.4} />
          </button>
        }
      />

      <div className="px-4 pt-2 space-y-3">
        <div data-testid="plan-subtabs" className="flex bg-secondary rounded-full p-1">
          {SUBTABS.map((s) => (
            <button
              key={s.id}
              data-testid={`plan-subtab-${s.id}`}
              onClick={() => setTab(s.id)}
              className={[
                "flex-1 h-9 rounded-full text-sm font-medium tap transition-colors",
                tab === s.id ? "bg-card text-foreground shadow-sm" : "text-muted-foreground",
              ].join(" ")}
            >
              {s.label}
            </button>
          ))}
        </div>
        {/* Persistent Kinly Quick-Add bar — visible on every Plan sub-tab */}
        <QuickAddBar familyId={family?.id} onCommitted={() => setRefreshKey(k => k + 1)} />
      </div>

      <main className="px-4 pt-4 pb-10" data-testid="surface-plan">
        {tab === "calendar" && <CalendarView refreshKey={refreshKey} onCreateAt={openCreateAt} />}
        {tab === "chores" && <ChoresView refreshKey={refreshKey} />}
        {tab === "polls" && <PollsView refreshKey={refreshKey} />}
        {tab === "todos" && <TodosView refreshKey={refreshKey} onCreated={() => setRefreshKey(k => k+1)} />}
      </main>

      <CreateSheet
        open={createOpen}
        tab={tab}
        seedAt={createSeedAt}
        onClose={() => { setCreateOpen(false); setCreateSeedAt(null); }}
        onCreated={onCreated}
      />
    </>
  );
}

// Re-export EmptyHint/EmptyState for any external imports (defensive — no current consumers)
export { EmptyHint, EmptyState } from "./Plan/Shared";
